'use client';

import { faChevronUp, faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Link from 'next/link';
import { RefObject, useCallback, useRef, useState } from 'react';
import { NavItem } from './nav-item';
import { useOnClickOutside } from 'usehooks-ts';

// useOnClickOutside has a rerender bug.
// eventListenerOptions are a hook dependency, so we pass an empty object so that the reference is the same on rerender.
// Without it, a new empty object is passed to a useEffect hook every render.
const eventListenerOptions = {};

const MegaMenu: React.FC<{
  items: NavItem[];
}> = ({
  items,
}) => {
    const [openListIndex, setOpenListIndex] = useState<number | null>(null);

    const openItem = openListIndex !== null ? items[openListIndex] : null;
    const openList = openItem && openItem.type === 'list' ? openItem : null;

    const closeList = useCallback(() => { setOpenListIndex(null); }, []);

    const ref = useRef<HTMLElement>(null);
    useOnClickOutside(
      ref as RefObject<HTMLElement>,
      closeList,
      undefined,
      eventListenerOptions,
    );

    return (
      <nav ref={ref} className={`dropdown ${openList !== null ? 'dropdown-open' : ''}`}>
        <ul className="menu menu-horizontal menu-lg">
          {items.map((item, i) => {
            const render = () => {
              switch (item.type) {
                case 'link': {
                  return (
                    <Link href={item.href} onClick={closeList}>
                      {item.label}
                    </Link>
                  );
                }

                case 'list': {
                  const toggle = () => {
                    setOpenListIndex((s) => s === i ? null : i);
                  };

                  const isOpen = openListIndex === i;

                  return (
                    <button type="button" onClick={toggle}>
                      {item.label}

                      <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} className="text-xs" />
                    </button>
                  );
                }
              }
            };

            return (
              <li key={i}>
                {render()}
              </li>
            );
          })}
        </ul>

        {openList ? (
          <div className="dropdown-content bg-base-100 border border-base-200 w-lg rounded-box z-1 p-2 shadow-2xl">
            <ul className="menu grid grid-cols-2 gap-4">
              {openList.links.map((link, i) => (
                <li key={i}>
                  <Link href={link.href} className="flex flex-col gap-1 items-start" onClick={closeList}>
                    <span className="text-lg font-semibold">
                      {link.label}
                    </span>

                    {link.description ? (
                      <span className="text-xs opacity-80">
                        {link.description}
                      </span>
                    ) : null}
                  </Link>
                </li>
              ))}
            </ul>

            <div className="card bg-base-200">
              <div className="card-body">
                <p className="font-medium text-base-content/80">
                  We are the one-stop salon software solution. Talk to us and find out how we can help your business thrive.
                </p>

                <Link href="/contact-sales" className="link link-primary" onClick={closeList}>
                  Contact sales
                </Link>
              </div>
            </div>
          </div>
        ) : null}
      </nav>
    );
  };

export default MegaMenu;
